import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useMemo } from 'react'
import { authClient } from '../lib/auth-client'
import { Logo } from '@/components/brand/logo'
import { BrandButton } from '@/components/brand/brand-button'

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || '',
    error: (search.error as string) || '',
  }),
  component: ResetPasswordPage,
})

function getPasswordStrength(password: string): { score: number; label: string; color: string; kmColor: string } {
  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^a-zA-Z0-9]/.test(password)) score++

  if (score <= 1) return { score: 1, label: 'Weak', color: 'bg-red-500', kmColor: '#ff6e84' }
  if (score <= 2) return { score: 2, label: 'Fair', color: 'bg-orange-500', kmColor: '#f59e0b' }
  if (score <= 3) return { score: 3, label: 'Medium', color: 'bg-yellow-500', kmColor: '#fbbf24' }
  if (score <= 4) return { score: 4, label: 'Strong', color: 'bg-green-500', kmColor: '#2b9a66' }
  return { score: 5, label: 'Very Strong', color: 'bg-green-600', kmColor: '#2b9a66' }
}

function ResetPasswordPage() {
  const { token, error: tokenError } = Route.useSearch()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [invalidToken, setInvalidToken] = useState(!token || !!tokenError)

  const passwordStrength = useMemo(() => getPasswordStrength(password), [password])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const { error: apiError } = await authClient.resetPassword({
        newPassword: password,
        token,
      })
      if (apiError) {
        const message = apiError.message || 'Failed to reset password'
        if (message.toLowerCase().includes('token') || message.toLowerCase().includes('expired')) {
          setInvalidToken(true)
        } else {
          setError(message)
        }
      } else {
        setSuccess(true)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password'
      if (message.toLowerCase().includes('token') || message.toLowerCase().includes('expired')) {
        setInvalidToken(true)
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <main
      id="main-content"
      className="min-h-svh flex items-center justify-center px-4"
      style={{ backgroundColor: "#0a0a0a" }}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size="sm" />
        </div>

        <div
          className="p-8"
          style={{ backgroundColor: "#111111", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
        >
          {success ? (
            <div className="text-center space-y-4">
              <div
                className="w-16 h-16 flex items-center justify-center mx-auto"
                style={{ backgroundColor: "rgba(43,154,102,0.1)" }}
              >
                <span
                  className="material-symbols-outlined"
                  aria-hidden="true"
                  style={{ fontSize: "32px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}
                >
                  check_circle
                </span>
              </div>
              <h2 className="text-2xl font-semibold" style={{ color: "#ffffff" }}>
                Password updated
              </h2>
              <p style={{ color: "#888888" }}>
                Your password has been reset successfully.
              </p>
              <div className="pt-4">
                <BrandButton asChild className="w-full">
                  <Link to="/login">Sign in</Link>
                </BrandButton>
              </div>
            </div>
          ) : invalidToken ? (
            <div className="text-center space-y-4">
              <div
                className="w-16 h-16 flex items-center justify-center mx-auto"
                style={{ backgroundColor: "rgba(255,110,132,0.1)" }}
              >
                <span
                  className="material-symbols-outlined"
                  aria-hidden="true"
                  style={{ fontSize: "32px", color: "#ff6e84", fontVariationSettings: "'FILL' 1" }}
                >
                  cancel
                </span>
              </div>
              <h2 className="text-2xl font-semibold" style={{ color: "#ffffff" }}>
                Reset link expired
              </h2>
              <p style={{ color: "#888888" }}>
                This password reset link is invalid or has expired.
              </p>
              <div className="pt-4">
                <BrandButton asChild className="w-full">
                  <Link to="/forgot-password">Request a new link</Link>
                </BrandButton>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-2" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Account Security</p>
                <h2 id="reset-heading" className="text-2xl font-semibold" style={{ color: "#ffffff" }}>
                  Set a new password
                </h2>
                <p className="mt-2" style={{ color: "#888888" }}>
                  Choose a strong password for your account.
                </p>
              </div>

              {error && (
                <div
                  role="alert"
                  className="mb-4 px-4 py-3 text-sm"
                  style={{
                    border: "1px solid rgba(255,110,132,0.2)",
                    borderLeft: "3px solid #ff6e84",
                    backgroundColor: "rgba(255,110,132,0.08)",
                    color: "#ff6e84",
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} aria-labelledby="reset-heading" className="space-y-5">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-bold mb-1.5"
                    style={{ color: "#ffffff" }}
                  >
                    New password
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>lock</span>
                    <input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
                      style={{
                        border: "1px solid rgba(255,255,255,0.1)",
                        backgroundColor: "#0a0a0a",
                        color: "#ffffff",
                      }}
                      placeholder="Enter a new password"
                    />
                  </div>
                  {password && (
                    <div className="mt-2">
                      <div
                        className="flex gap-1 mb-1"
                        role="meter"
                        aria-valuenow={passwordStrength.score}
                        aria-valuemin={1}
                        aria-valuemax={5}
                        aria-label={`Password strength: ${passwordStrength.label}`}
                      >
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className="h-1 flex-1 transition-colors"
                            style={{
                              backgroundColor: level <= passwordStrength.score
                                ? passwordStrength.kmColor
                                : "rgba(255,255,255,0.08)",
                            }}
                          />
                        ))}
                      </div>
                      <p
                        className="text-xs text-right"
                        aria-live="polite"
                        style={{ color: passwordStrength.kmColor }}
                      >
                        {passwordStrength.label}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-bold mb-1.5"
                    style={{ color: "#ffffff" }}
                  >
                    Confirm password
                  </label>
                  <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" style={{ fontSize: "16px", color: "#888888" }}>lock</span>
                    <input
                      id="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full pl-10 pr-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/50"
                      style={{
                        border: "1px solid rgba(255,255,255,0.1)",
                        backgroundColor: "#0a0a0a",
                        color: "#ffffff",
                      }}
                      placeholder="Confirm your new password"
                    />
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs" style={{ color: "#ff6e84" }}>Passwords do not match</p>
                  )}
                </div>

                <BrandButton
                  type="submit"
                  disabled={loading || password !== confirmPassword}
                  aria-busy={loading}
                  className="w-full"
                  size="lg"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </BrandButton>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  )
}
